import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import type { SocialPostsImport } from '@/types/resources';
import { api } from '@/utils/api';
import { SocialPostsImportStatus } from '@/utils/enums';

const Tr = styled.tr`
  cursor: pointer;
  &:hover {
    > td {
      background-color: rgb(251, 251, 251) !important;
    }
  }
`;

const Status = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  text-transform: capitalize;
`;

type StatusDotProps = {
  $status: SocialPostsImportStatus;
};

const StatusDot = styled.div<StatusDotProps>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-top: 5px;

  ${({ theme, $status }) => css`
    background: ${$status === SocialPostsImportStatus.PENDING
      ? theme.colors.warning
      : theme.colors.success};
  `}
`;

const getStatusLabel = (status: SocialPostsImportStatus) => {
  if (status === SocialPostsImportStatus.EXPORTED) {
    return 'Exported';
  }

  if (status === SocialPostsImportStatus.DONE) {
    return 'Done';
  }

  return 'Pending';
};

interface Props {
  postsImport: SocialPostsImport;
}

const TableRow: FC<Props> = ({ postsImport }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const onDelete = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to delete this import?`,
      );

      if (!confirm) return;

      await api.delete(`/v1/tools/social-posts-imports/${postsImport.id}`);

      await queryClient.invalidateQueries(['social_posts_imports']);

      alerts.success('Import was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <Tr onClick={() => navigate(postsImport.id.toString())}>
      <Td>
        <Text
          as="a"
          $type="smallBold"
          target="_blank"
          href={postsImport.targetUrl}
          rel="noreferrer noopener"
          onClick={(e: MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
        >
          {postsImport.targetUrl}
        </Text>
      </Td>
      <Td>{postsImport.createdBy ?? '-'}</Td>
      <Td>
        <Status>
          <StatusDot $status={postsImport.status} />
          <span>{getStatusLabel(postsImport.status)}</span>
        </Status>
      </Td>
      <Td>{DateTime.fromISO(postsImport.createdAt).toFormat('yyyy/LL/dd')}</Td>
      <Td>{postsImport.minLikes}</Td>
      <Td>{postsImport.postsSince ? DateTime.fromISO(postsImport.postsSince).toFormat('yyyy/LL/dd') : "-"}</Td>
      <Td>{postsImport.resultsLimit}</Td>
      <Td>{postsImport.savablePostsCount}</Td>
      <Td>{postsImport.postsImported ?? 0}</Td>
      <Td $actionsCol onClick={(e) => e.stopPropagation()}>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default TableRow;
